import dayjs from 'dayjs'
import type { TeamPaymentDate } from '@sportninja/common/services/teamBalance'
import { balanceText } from './balanceI18n'

/**
 * The driving date of a payment cutoff, formatted for copy.
 *
 * Reads the server's `on` — the calendar day the cutoff falls on in the
 * COMPETITION's timezone — NOT the `at` instant. The two disagree: competition
 * start is anchored to midnight in the account's zone, so `at` for a Sep 13 start
 * is 2026-09-13T07:00:00Z for a Vancouver account and 2026-09-12T15:00:00Z for a
 * Tokyo one. Formatting that instant in the VIEWER's zone (or in UTC) prints the
 * wrong day for anyone outside the anchoring zone — a rep in Vancouver saw "Sep 12"
 * while the competition header on the same screen said "Sep 13". Only the server
 * knows the zone, so only the server can name the day.
 *
 * `on` is a bare `YYYY-MM-DD`, which dayjs parses as LOCAL midnight — no zone shift
 * is possible, so every viewer renders the same day.
 *
 * Falls back to `at` when `on` is absent (payload from an older server). An
 * unparseable value yields null so callers fall back to their date-free copy
 * rather than printing "Invalid Date".
 *
 * SHARED on purpose: the status badge and the season runway name the same dates,
 * and this rule is subtle enough that a second implementation is how one of them
 * silently goes a day out.
 *
 * Takes the widened `TeamPaymentDate` so the window's OPENING day — which is not a
 * cutoff and carries no `source` — is formatted by the same rule as the deadlines.
 */
export const formatCutoff = (
  cutoff: TeamPaymentDate | undefined | null
): string | null => {
  const value = cutoff?.on ?? cutoff?.at
  if (!value) {
    return null
  }
  const parsed = dayjs(value)

  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null
}

/**
 * SN-8310 — a competition day (`YYYY-MM-DD`, the server's `on`) in the Team
 * Budget header's style: "Sat, Sep 26", or "sam. 26 sept." in French. The
 * pattern is translated so each language keeps its own order.
 *
 * Same parsing rule as `formatCutoff`: a bare day parses as LOCAL midnight, so
 * no viewer's zone can move it. An unparseable value is returned as-is rather
 * than printed as "Invalid Date".
 */
export const formatStandingDay = (day: string): string => {
  const parsed = dayjs(day)

  return parsed.isValid()
    ? parsed.format(balanceText('common:balanceStandingDateFormat'))
    : day
}
